/**
 * AdminChatThread — Rechte Spalte / Detail-Ansicht im Messenger-Layout
 *
 * Lädt + zeigt Messages einer Konversation, mit:
 *   - Header: User-Info (Display-Name, Plan, optional Email)
 *   - Body: Messages (Reuse der ChatMessageBubble von der User-Seite)
 *   - Footer: Eingabefeld (Reuse der ChatInput-Komponente)
 *   - Auto-Scroll bei neuer Nachricht
 *   - Auto-Mark-Read beim Öffnen
 *
 * Empty-State (keine Konversation ausgewählt): freundlicher Hinweis.
 *
 * Mobile-Variante: zeigt zusätzlich einen "Zurück"-Button im Header,
 * damit User vom Thread zurück zur Liste navigieren können.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, MessageSquare } from 'lucide-react';
import { useAdminChatThread } from '../../../hooks/useAdminChatThread';
import { ChatMessageBubble } from '../../support/ChatMessageBubble';
import { ChatInput } from '../../support/ChatInput';
import type { PendingReply } from '../../support/ChatPanel';
import type { UIMessage } from '../../../hooks/useChatConversation';

interface AdminChatThreadProps {
  conversationId: string | null;
  /** Auf Mobile: Callback für "Zurück zur Liste". Auf Desktop: undefined → kein Button. */
  onBack?: () => void;
}

export function AdminChatThread({ conversationId, onBack }: AdminChatThreadProps): React.ReactElement {
  const { conversation, messages, isLoading, error, send } = useAdminChatThread({
    conversationId
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const isUserScrolledUpRef = useRef(false);
  const lastMessageRef = useRef<UIMessage | null>(null);

  // Pending-Reply (Admin-Pfad): User wischt auf eine Bubble → Reply-Modus.
  const [pendingReply, setPendingReply] = useState<PendingReply | null>(null);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const highlightTimerRef = useRef<number | null>(null);

  // Reply zurücksetzen, wenn der Admin die Konversation wechselt.
  useEffect(() => {
    setPendingReply(null);
  }, [conversationId]);

  // Auto-Scroll bei neuer Nachricht (außer User hat manuell gescrollt).
  // EXCEPTION: Wenn der ADMIN selbst eine neue Nachricht hinzugefügt hat
  // (Optimistic-Bubble), immer scrollen.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const newest = messages[messages.length - 1];
    const wasMine = newest && newest.senderType === 'admin';
    const prevLast = lastMessageRef.current;
    const isOwnNew =
      wasMine &&
      (!prevLast || prevLast.id !== newest.id) &&
      (!prevLast || prevLast.clientId !== newest.clientId);
    lastMessageRef.current = newest ?? null;

    if (isUserScrolledUpRef.current && !isOwnNew) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
        if (isOwnNew) isUserScrolledUpRef.current = false;
      });
    });
  }, [messages]);

  // Bei Wechsel der Konversation: Scroll-Indicator zurücksetzen
  useEffect(() => {
    isUserScrolledUpRef.current = false;
    lastMessageRef.current = null;
  }, [conversationId]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>): void => {
    const el = e.currentTarget;
    isUserScrolledUpRef.current = el.scrollHeight - el.clientHeight - el.scrollTop > 80;
  };

  const handleJumpToMessage = useCallback((messageId: string) => {
    const container = scrollRef.current;
    if (!container) return;
    const target = container.querySelector<HTMLElement>(
      `[data-message-id="${CSS.escape(messageId)}"]`
    );
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    isUserScrolledUpRef.current = true;
    setHighlightedId(messageId);
    if (highlightTimerRef.current !== null) {
      window.clearTimeout(highlightTimerRef.current);
    }
    highlightTimerRef.current = window.setTimeout(() => {
      setHighlightedId(null);
      highlightTimerRef.current = null;
    }, 1500);
  }, []);

  useEffect(() => () => {
    if (highlightTimerRef.current !== null) {
      window.clearTimeout(highlightTimerRef.current);
    }
  }, []);

  // Empty-State: keine Konversation ausgewählt (Desktop only — auf Mobile sieht man die Liste)
  if (!conversationId) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-6 text-center">
        <MessageSquare className="w-10 h-10 opacity-20 mb-3" strokeWidth={1.5} />
        <p className="text-[14px] opacity-50 font-mono">Wähle links eine Konversation aus.</p>
      </div>
    );
  }

  if (isLoading && messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-[12px] opacity-40 font-mono">Lädt …</p>
      </div>
    );
  }

  if (error && messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full px-6 text-center">
        <p className="text-[13px] opacity-60 font-mono">
          Konversation konnte nicht geladen werden.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full font-mono">
      {/* Header mit User-Info */}
      {conversation && (
        <header className="flex items-center gap-3 px-4 py-3 border-b border-white/5 dark:border-white/5 shrink-0">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label="Zurück zur Liste"
              className="
                shrink-0 w-8 h-8 rounded-full
                flex items-center justify-center
                opacity-60 hover:opacity-100
                hover:bg-white/5 dark:hover:bg-white/5
                transition-all duration-150
                focus:outline-none focus:ring-2 focus:ring-brand/40
              "
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={2} />
            </button>
          )}

          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold truncate">
              {conversation.userDisplayName}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              {conversation.planSlugAtCreation && (
                <span className="text-[10px] uppercase tracking-wider opacity-50">
                  {conversation.planSlugAtCreation}
                </span>
              )}
              {conversation.userEmail && (
                <span className="text-[11px] opacity-40 truncate">{conversation.userEmail}</span>
              )}
            </div>
          </div>
        </header>
      )}

      {/* Body — Messages */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        data-lenis-prevent
        className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain scrollbar-styled touch-pan-y px-3 py-3"
      >
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-[12px] opacity-40">Noch keine Nachrichten.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {messages.map((m) => (
              <ChatMessageBubble
                key={m.clientId ?? m.id}
                message={m}
                viewerType="admin"
                isHighlighted={highlightedId === m.id}
                onJumpToMessage={handleJumpToMessage}
                onReply={() => setPendingReply({
                  messageId: m.id,
                  senderType: m.senderType,
                  contentPreview: m.content.trim().slice(0, 80),
                  hasAttachments: m.attachments.length > 0
                })}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer — Eingabefeld */}
      <ChatInput
        onSend={(content, images, captions, replyToMessageId) =>
          send(content, {
            ...(images ? { images } : {}),
            ...(captions ? { captions } : {}),
            ...(replyToMessageId ? { replyToMessageId } : {})
          })
        }
        pendingReply={pendingReply}
        onClearReply={() => setPendingReply(null)}
      />
    </div>
  );
}
